/**
 * src/engine/jobs/createTaskIfMissing.js
 *
 * Handler for state_logic operation: create_task_if_missing
 *
 * For each matched entity, creates a linked engine-generated task — but
 * skips entities that already have an open engine-generated task with the
 * same job id. This is the idempotency guard required by ENG-B-004.
 *
 * Returns { changed: bool, summary: string, newTasks: object[] }
 */

import { generateId, now } from '../../utils/formatting.js';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Read `dueDays` off a stored job config without trusting it.
 *
 * The form that writes this field advertised `min=0` and never enforced it, so
 * job records already in tenant state can carry a negative offset — which
 * produces a task that is overdue the moment it is created, quietly inflating
 * the Action Centre and every overdue-task derivation — or a NaN, which makes
 * `new Date(NaN).toISOString()` throw and aborts the whole job run partway
 * through, leaving some entities with tasks and the rest without (#5364).
 *
 * Clamping here rather than only in the form is the point: the bad values are
 * already persisted, and this is the code that has to survive them.
 */
function safeDueDays(raw, fallback = 14) {
  // Number(null) and Number('') are both 0, which would silently reinterpret
  // "not configured" as "due today". Only an explicit 0 means today.
  if (raw == null || raw === '') return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(Math.trunc(n), 0);
}

function interpolate(template, entity) {
  if (!template) return '';
  return template.replace(/\{\{entity\.(\w+)\}\}/g, (_, key) => entity[key] ?? '');
}

function openTaskExists(state, jobId, entityId) {
  return (state.adhocTasks || []).some(
    (t) =>
      t.engineGenerated &&
      t.sourceJobId === jobId &&
      t.linkedEntityId === entityId &&
      t.status !== 'closed' &&
      t.status !== 'completed'
  );
}

export function runCreateTaskIfMissing(job, actionConfig, matchedEntities, state) {
  const {
    taskTitle = 'Review: {{entity.name}}',
    taskDescription = 'Auto-generated by engine job.',
    taskType = 'automatic',
    dueDays = 14,
    linkToEntity = true,
  } = actionConfig || {};

  const newTasks = [];
  const skipped = [];

  for (const entity of matchedEntities) {
    if (linkToEntity && openTaskExists(state, job.id, entity.id)) {
      skipped.push(entity.name || entity.id);
      continue;
    }

    const dueDate = new Date(Date.now() + safeDueDays(dueDays) * MS_PER_DAY).toISOString();

    newTasks.push({
      id: 'task_' + generateId(),
      title: interpolate(taskTitle, entity),
      description: interpolate(taskDescription, entity),
      type: 'automatic',
      taskType,
      status: 'open',
      dueDate,
      assigneeId: entity.ownerId || null,
      controls: entity.controls || [],
      linkedEntityId: linkToEntity ? entity.id : null,
      linkedEntityType: linkToEntity ? entity._entityType : null,
      engineGenerated: true,
      sourceJobId: job.id,
      createdAt: now(),
      evidence: [],
    });
  }

  const changed = newTasks.length > 0;
  let summary;
  if (changed && skipped.length === 0) {
    summary = `Created ${newTasks.length} task(s)`;
  } else if (changed && skipped.length > 0) {
    summary = `Created ${newTasks.length} task(s); skipped ${skipped.length} (open task already exists)`;
  } else if (skipped.length > 0) {
    summary = `No new tasks — ${skipped.length} entit${skipped.length === 1 ? 'y' : 'ies'} already have an open task`;
  } else {
    summary = 'No matched entities — nothing to do';
  }

  return { changed, summary, newTasks };
}
